import { getBootstrapIconUrl, BootstrapIcon } from '../Global/icons'
import { tryLoadDependencies, tryLoadLightDOMStyles, WebAwesomeComponent } from '../Global/loadDependency'
import { getTemplateNotificationBannerStyles } from './NotificationBanner.styles'
const XIcon = getBootstrapIconUrl(BootstrapIcon.X)


const t = document.createElement('template')
t.innerHTML = `
<div class="notification-banner">
  <wa-icon name="circle-info"></wa-icon>
  <div class="notification-content"><slot></slot></div>
  <div class="close-button" aria-label="Close notification" role="button" tabindex="0">
    <wa-icon src="${XIcon}" aria-hidden="true"></wa-icon>
  </div>
</div>
`

class NotificationBanner extends HTMLElement {
  private notificationBanner: HTMLElement | null = null
  private waIcon: HTMLElement | null = null
  private closeButton: HTMLElement | null = null

  constructor() {
    super()

    tryLoadDependencies(document, [WebAwesomeComponent.Icon])

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplateNotificationBannerStyles().content.cloneNode(true))

    shadow.appendChild(t.content.cloneNode(true))

    tryLoadLightDOMStyles('NotificationBanner.css')

    this.notificationBanner = shadow.querySelector('.notification-banner')
    this.waIcon = shadow.querySelector('wa-icon')
    this.closeButton = shadow.querySelector('.close-button')

    this.setupCloseButton()
  }
  static get observedAttributes() {
    return ['variant', 'dismissible', 'dismiss-duration']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'variant' && this.notificationBanner) {
      this.updateIcon(newValue)
    }
    if (name === 'dismissible') {
      this.setDismissible(newValue !== null && newValue !== 'false')
    }
  }

  private updateIcon(variant: string) {
    if (!this.waIcon) return

    const iconMap: Record<string, string> = {
      'primary': 'info-circle',
      'success': 'check-circle',
      'neutral': 'info-circle',
      'warning': 'exclamation-triangle',
      'danger': 'exclamation-triangle'
    }

    const iconName = iconMap[variant] || 'info-circle'
    this.waIcon.setAttribute('name', iconName)
  }
  connectedCallback() {
    // Apply initial variant and dismissible state
    const variant = this.getAttribute('variant') || 'neutral'
    this.updateIcon(variant)
    this.setDismissible(this.hasAttribute('dismissible') && this.getAttribute('dismissible') !== 'false')
  }

  private setupCloseButton() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        const duration = parseInt(this.getAttribute('dismiss-duration') || '300')
        this.performAnimatedRemove(duration)
      })
    }
  }

  private performAnimatedRemove(duration: number = 300) {
    // Fire dismissal event first
    this.dispatchEvent(new CustomEvent('ts-dismiss', {
      detail: {
        variant: this.getAttribute('variant') || 'neutral'
      },
      bubbles: true
    }))

    // Animate using CSS class then remove
    if (this.notificationBanner) {
      // Set duration as CSS custom property for dynamic timing
      this.notificationBanner.style.setProperty('--dismiss-duration', `${duration}ms`)

      // Apply dismissing class for CSS animation
      this.notificationBanner.classList.add('dismissing')

      setTimeout(() => {
        try {
          this.remove()
        } catch {
          // Ignore removal errors (e.g., in test environments)
        }
      }, duration)
    } else {
      try {
        this.remove()
      } catch {
        // Ignore removal errors (e.g., in test environments)
      }
    }
  }

  // Public method for immediate removal
  public removeElement() {
    this.dispatchEvent(new CustomEvent('ts-dismiss', {
      detail: {
        variant: this.getAttribute('variant') || 'neutral'
      },
      bubbles: true
    }))

    try {
      this.remove()
    } catch {
      // Ignore removal errors (e.g., in test environments)
    }
  }

  private setDismissible(dismissible: boolean) {
    if (this.closeButton) {
      // Use !important to override any CSS rules
      this.closeButton.style.setProperty('display', dismissible ? 'flex' : 'none', 'important')
    }
  }

}

customElements.define('ts-notificationbanner', NotificationBanner)
export { NotificationBanner }
